import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import integrationsService from '../services/integrationsService';
import legacyUapiService from '../services/LegacyUapiService';
import hotelService from '../services/hotelService';
import Button from './shared/Button';
import SearchSelect from './shared/SearchSelect';
import Card from './shared/Card';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import IntegrationDesigner from './IntegrationDesigner';
import ImplementationBoard from './shared/ImplementationBoard';
import { ImplementationStatusSelect, ImplementationVersionSelect } from './shared/ImplementationControls';
import useRouteGrants from './shared/useRouteGrants';
import PickSystemModal from './shared/PickSystemModal';
import VersionsTable from './shared/VersionsTable';
import Tabs from './shared/Tabs';
import PipelinesHeader from './pipelines/PipelinesHeader';
import { Workflow, Building2, Plus, Trash2, ChevronDown, ChevronRight, CircleDot, RotateCcw } from 'lucide-react';
import './ConnectorForm.css';
import './IntegrationForm.css';
import './shared/ImplementationWizard.css';   // .iw-empty — an implementation's unfilled count
import './PaymentPorterPage.css';           // .pp-back-btn
import { apiErrorMessage } from '../utils/apiError';
import { formatDateTime } from '../utils/datetime';

function IntegrationForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { selectedAccount, selectedPartner, selectedHotel, accounts, partnerData } = useAccount();
  const { isSuperAdmin } = useAuth();
  // WHICH MODE, AT THE TOP. These are derived from a prop and nothing else, and effects far above their
  // old home read them — a const read before its own declaration is a TDZ crash on first render, not an
  // undefined, so where they sit is load-bearing.
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  // Return to Integrate Systems when the form was opened from it, otherwise the integrations admin
  // list. `esb` is still accepted: it is what the old screen sent, and a link made before the rename
  // should still come back to the right place rather than to a list the reader did not start on.
  const from = new URLSearchParams(location.search).get('from');
  // THE OLD VALUES STILL RESOLVE. `from` travels in a URL somebody may have bookmarked or pasted
  // into a ticket, so the two names this screen used to have send Back to the same place its
  // current name does, rather than to the template list.
  // A TEMPLATE FOR AN MCP AS A SERVICE SOURCE SYSTEM (V644) — opened from Admin › Platform › MCP as a Service › the
  // system with ?from=mcp-source&mcpSource=<id>. It belongs to that system, not to the chain in the top bar, and Back
  // returns to the system's page. Editing, the record's own mcp_source_system_id says the same (templateMcpSource).
  const mcpSourceParam = new URLSearchParams(location.search).get('mcpSource');
  // OPENED FROM ONE HOTEL'S PIPELINE (?from=implementation&returnTo=/pipelines/…): Back goes back to it. Only a path
  // under /pipelines/ is followed — a link someone edits cannot send Back off the site.
  const returnToParam = new URLSearchParams(location.search).get('returnTo');
  const backToImplementation = from === 'implementation' && /^\/pipelines\/\d+\/implementations\/\d+$/.test(returnToParam || '');
  const fromPipelinesList = ['pipelines', 'integrate-systems', 'esb'].includes(from);
  // OPENED FROM THE PIPELINES PAGE — its template list or a hotel's pipeline — the Pipelines header and tab bar stay,
  // with Pipeline Templates lit, so the user has not left the screen they clicked on.
  const inPipelines = backToImplementation || fromPipelinesList;
  const returnPath = backToImplementation ? returnToParam
      : from === 'mcp-source' && mcpSourceParam
      ? `/admin/mcp-service/sources/${mcpSourceParam}`
      // The Pipelines page opens on its Hotels tab; a template is listed on Pipeline Templates (/pipeline-templates
      // redirects there too).
      : '/pipelines?tab=templates';
  // What the Back link at the top names: the page returnPath goes to.
  const returnLabel = from === 'mcp-source' && mcpSourceParam ? null
      : backToImplementation ? 'the hotel\'s pipeline'
      : returnPath.startsWith('/pipelines?') ? 'Pipelines' : 'Pipeline templates';

  // AS WHOM A ROUTE IS MADE. The credentials issued to the hotel chosen in the top bar; the credential is
  // what decides which backend a route reaches. The modules a route can carry come with each system in the
  // designer's palette, so nothing else has to be fetched here — and the several-hundred-entry operation
  // list this used to load is no longer read by anything.
  const [uapiCredentials, setUapiCredentials] = useState([]);

  // Re-read when the chosen property changes: a credential belongs to one hotel, and offering another
  // hotel's would write a call this integration cannot make.
  useEffect(() => {
    let cancelled = false;
    if (!selectedHotel && !selectedAccount) { setUapiCredentials([]); return undefined; }
    legacyUapiService.listCredentials({ hotelId: selectedHotel, accountId: selectedHotel ? undefined : selectedAccount })
      .then(res => {
        if (cancelled) return;
        // The endpoint answers per HOTEL — one row each, carrying that hotel's credential. Rows without
        // one are hotels that have not been issued a credential, and are not choices.
        const rows = (res.data?.credentials || res.data || []).filter(r => r.credential_id);
        setUapiCredentials(rows.map(r => ({
          id: r.credential_id,
          credential_name: r.credential_name || r.hotelname || `Credential #${r.credential_id}`,
          // The property the credential belongs to. The Universal API's authentication screen is addressed
          // by HOTEL, not by credential, so this is what a link to it needs.
          hotel_id: r.hotel_id,
          // WHICH BACKEND THIS CREDENTIAL REACHES. The endpoint already answers it — the property's
          // uapi_hotel_access bindings — pipe-separated. Carried here so a route drawn in the browser names
          // its destination immediately, instead of saying "resolved by the credential" until it is saved
          // and read back.
          backend_systems: (r.access_systems || '').split('|').filter(Boolean).join(', ') || null,
        })));
      })
      .catch(() => { if (!cancelled) setUapiCredentials([]); });
    return () => { cancelled = true; };
  }, [selectedHotel, selectedAccount]);

  // THE BACKENDS THIS PROPERTY IS ROUTED TO (uapi_hotel_access, V419).
  //
  // A route goes to ONE of these: each row is a backend system, the credential the Universal API calls it
  // with, and the x-hotelid that picks it. A credential can appear on several rows, which is exactly why a
  // route names the binding and not just the credential (V484).
  const [uapiAccess, setUapiAccess] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!selectedHotel) { setUapiAccess([]); return undefined; }
    legacyUapiService.listAccess(selectedHotel)
      .then(res => { if (!cancelled) setUapiAccess(res.data?.access || res.data || []); })
      .catch(() => { if (!cancelled) setUapiAccess([]); });
    return () => { cancelled = true; };
  }, [selectedHotel]);


  // Names for the scope shown beside the title. Resolved the same way the top bar resolves them — from the
  // same context lists — so a rename or a switch is reflected in both at once. Each falls back to nothing
  // rather than to an id: a number beside a title is noise to the person reading it.
  const scopePartnerName = (partnerData && (partnerData.companyname || partnerData.name)) || null;
  const scopeAccountName = selectedAccount
    ? ((accounts || []).find(a => a.id === selectedAccount)?.name || null)
    : null;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    v2_connector_from: '',
    v2_connector_to: ''
  });
  // A TEMPLATE BELONGS TO A CHAIN (V524), NOT A HOTEL. It is created against the chain chosen in the top
  // bar, and the hotels it runs at are its implementations — added on the Implementations tab, one per
  // property. Editing, this is the chain the record holds, which the top bar may not be showing.
  const [templateChainName, setTemplateChainName] = useState(null);
  const [templateAccountId, setTemplateAccountId] = useState(null);
  // WHETHER THIS CALLER MAY CHANGE THE TEMPLATE — the server's own ownership check (db.canWrite), returned with
  // the record, so Delete is offered to exactly those the DELETE route lets through.
  const [canWrite, setCanWrite] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [templateMcpSource, setTemplateMcpSource] = useState(null);   // { id, name } when the record is a source system's
  const isMcpSourceTemplate = isCreateMode ? !!mcpSourceParam : !!(templateMcpSource || mcpSourceParam);

  // THE CANVAS. Held whole — nodes, edges and the palette of systems — because that is how it is read
  // and written: a picture is one thing, and sending half of it would mean the server deciding which
  // half to keep.
  // EVERY DESIGN HAS A HUB, so one is there from the first render rather than being asked for. An
  // integration meets at the Universal API by definition; making somebody add it would be a question
  // with one answer.
  const [graph, setGraph] = useState({
    nodes: [{ id: 'hub', role: 'HUB', marketplace_id: null, adapter: null, label: null }],
    edges: [],
    systems: [],
    routes: [],
    customCode: [],
  });
  const [picking, setPicking] = useState(null);     // 'SOURCE' | 'TARGET' while the picker is open
  const [graphDirty, setGraphDirty] = useState(false);
  // THE SHAPE CHANGED — a source added or removed, a route drawn or changed — which is what changes the slots an
  // implementation fills. Kept apart from graphDirty because the designer generates a proxy key for a proxied
  // source that has none the moment the board loads (a source whose system became proxied after the template was
  // saved): that is unsaved, but it changes no slot, and it was disabling Add on a template nobody had touched.
  const [shapeDirty, setShapeDirty] = useState(false);
  // The source's Events screen (V686) — the template's, so it needs the template saved.
  const openSourcePage = (page) => {
    if (!id || graphDirty) {
      setError('Save the template first — leaving now would lose the unsaved design changes.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(`/pipeline-templates/${id}/${page}${location.search || ''}`);
  };
  // ── IMPLEMENTATIONS (V508) ──────────────────────────────────────────────────────────────────────
  // The design above is a TEMPLATE — which systems, which proxies, which routes. An implementation is
  // that template at one property, with that property's credentials. `slots` is what the template
  // needs filled and is derived server-side from the design, so a route added today produces an
  // unfilled slot in every implementation rather than being silently missing from them.
  // THE TAB IS IN THE URL. A tab that lives only in component state cannot be linked to, does not
  // survive a refresh, and is lost by Back — so "look at this template's versions" is not something one
  // person can send another. Read on arrival, written on every click.
  // Versions are NOT a tab: they are read beside the design, under the board. See the Versions block.
  // NO IMPLEMENTATIONS ON AN MCP SOURCE TEMPLATE. It is built for a PMS system, not run at hotels from here — a hotel
  // takes it up through its MCP as a Service set-up (/mcp-service), so the design is the whole of this page.
  // THE HOTELS A TEMPLATE RUNS AT ARE PIPELINES › HOTELS, and only there (2026-10-28). The template's own Implementations
  // tab — the hotel list from before Hotels became the way in — is not offered: two places to add and open a hotel's
  // pipeline was the confusion. The template is its design.
  const TABS = ['design'];
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return TABS.includes(t) ? t : 'design';
  });
  // The record can turn out to be a source system's after it loads (a link without ?mcpSource=), so fall back then too.
  useEffect(() => { if (isMcpSourceTemplate && tab !== 'design') setTab('design'); }, [isMcpSourceTemplate, tab]);
  // REPLACE, NOT PUSH: clicking between tabs while reading a template would otherwise stack history
  // entries to reverse before Back leaves the screen. Every other parameter is carried through — `from`
  // above all, since the Back button reads it to know where it came from.
  const changeTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const [implementations, setImplementations] = useState(null);   // null = not loaded
  const [slots, setSlots] = useState([]);

  // ── VERSIONS (V512) ───────────────────────────────────────────────────────
  //
  // The draft is the design on the Design tab; these are the snapshots of it and the pointer at
  // whichever one is live. Loaded when the tab is first opened, like the implementations.
  const [versions, setVersions] = useState(null);       // null = not loaded
  const [versionBusy, setVersionBusy] = useState(false);

  const loadVersions = useCallback(async () => {
    if (!id) return;
    try {
      setVersions(await integrationsService.listVersions(id));
    } catch (e) {
      setImplError(apiErrorMessage(e, 'Could not load this template\'s versions'));
      setVersions({ versions: [], liveVersionId: null });
    }
  }, [id]);

  // LOADED FOR THE DESIGN TAB, not only for the Versions one: the picker above the board lists them,
  // and the live badge beside the title is read from the same answer.
  useEffect(() => {
    if (!isCreateMode && versions === null) loadVersions();
  }, [isCreateMode, versions, loadVersions]);

  // ── WHICH DESIGN IS ON SCREEN ─────────────────────────────────────────────
  //
  // null is the DRAFT — the design itself, the one thing on this screen that can be edited. A published
  // version is opened to READ: the board is the same board, so a pane that accepted clicks it could
  // never save would be the worst way to find that out.
  const [viewingVersionId, setViewingVersionId] = useState(null);
  const [viewingVersion, setViewingVersion] = useState(null);   // null while loading

  useEffect(() => {
    let cancelled = false;
    if (!id || !viewingVersionId) { setViewingVersion(null); return undefined; }
    setViewingVersion(null);
    integrationsService.getVersion(id, viewingVersionId)
      .then(res => { if (!cancelled) setViewingVersion(res.version || null); })
      .catch(e => {
        if (cancelled) return;
        setImplError(apiErrorMessage(e, 'That version could not be opened'));
        setViewingVersionId(null);
      });
    return () => { cancelled = true; };
  }, [id, viewingVersionId]);

  // What the board draws: the frozen design when one is chosen, the draft otherwise.
  const shownGraph = viewingVersionId ? (viewingVersion && viewingVersion.graph) : graph;
  const viewingDraft = !viewingVersionId;

  // One runner for every version action: they all end in "and now show me the new list", and each
  // writing its own was four chances to forget to clear `busy` on a failure.
  const runVersionAction = async (fn, failure) => {
    setVersionBusy(true);
    setImplError(null);
    try {
      const res = await fn();
      if (res && res.versions) setVersions({ versions: res.versions, liveVersionId: res.liveVersionId });
      else await loadVersions();
      // Going live puts implementations not yet on a version onto it, and changes which one is "live" beside each.
      if (implementations !== null) loadImplementations();
    } catch (e) {
      setImplError(apiErrorMessage(e, failure));
    } finally {
      setVersionBusy(false);
    }
  };

  // ── THE OPEN IMPLEMENTATION'S BOARD ───────────────────────────────────────
  //
  // An implementation IS the design with the credentials in it, so it is drawn the way the design is
  // drawn — the same board, read-only, with each slot filled from that property's bindings. A row
  // saying "3 of 4" says how far along it is and nothing about what is plugged in where.
  //
  // ONE AT A TIME, fetched when opened: the bindings are only on the single-implementation endpoint,
  // and drawing every property's board at once would be a page of near-identical diagrams.

  const [openImplId, setOpenImplId] = useState(null);
  // The open implementation's board — its loading, its property's credentials and its binding saves — is the shared
  // ImplementationBoard's; this screen keeps the list, the status and the actions the board's boxes offer.
  const routeGrants = useRouteGrants(shownGraph && shownGraph.routes);
  const implSaved = useCallback((saved) => {
    // "3 of 4" is computed from the bindings, so the row above has to hear about a change on the board.
    setImplementations(list => (list || []).map(x => (x.id === saved.id
      ? { ...x, filled: (saved.bindings || []).filter(b => b.credential_id).length } : x)));
  }, []);
  const implBoardError = useCallback((msg, closed) => {
    setImplError(msg);
    if (closed) setOpenImplId(null);
  }, []);

  const [implError, setImplError] = useState(null);
  // Status alone (draft / active / paused) — its own endpoint, because the full save replaces the bindings.
  const [statusSaving, setStatusSaving] = useState(null);
  const changeImplStatus = async (im, status) => {
    if (status === im.status) return;
    setStatusSaving(im.id);
    setImplError(null);
    try {
      await integrationsService.setImplementationStatus(id, im.id, status);
      setImplementations(list => (list || []).map(x => (x.id === im.id ? { ...x, status } : x)));
    } catch (e) {
      setImplError(apiErrorMessage(e, 'The status could not be changed.'));
    } finally {
      setStatusSaving(null);
    }
  };
  // THE VERSION ONE PROPERTY ROUTES WITH (V672) — moved by hand, one implementation at a time, forwards or back.
  // Confirmed first: the next message for that property is routed with the version chosen.
  const [versionSaving, setVersionSaving] = useState(null);
  const publishedVersions = (versions && versions.versions) || [];
  const changeImplVersion = (im, versionId) => {
    const target = publishedVersions.find(v => String(v.id) === String(versionId));
    if (!target || String(versionId) === String(im.version_id)) return;
    setConfirmModal({
      title: `Move ${im.hotelname || im.name} to v${target.version}`,
      message: `${im.hotelname || 'This property'} routes with v${target.version} of the design from its next message`
        + `${im.version ? ` (it is on v${im.version} now)` : ''}. No other implementation changes.`,
      confirmLabel: `Move to v${target.version}`,
      onConfirm: async () => {
        setConfirmModal(null);
        setVersionSaving(im.id);
        setImplError(null);
        try {
          const res = await integrationsService.setImplementationVersion(id, im.id, target.id);
          const moved = (res && res.implementation) || {};
          setImplementations(list => (list || []).map(x => (x.id === im.id ? {
            ...x, version_id: moved.version_id ?? target.id, version: moved.version ?? target.version,
            pinned_version: target.version, version_pinned: 1,
          } : x)));
          loadVersions();   // the Implementations count on each version
        } catch (e) {
          setImplError(apiErrorMessage(e, 'The implementation could not be moved to that version.'));
        } finally {
          setVersionSaving(null);
        }
      },
    });
  };
  // ConfirmModal, never window.confirm (STYLE.md). Held as the modal's own props so one piece of
  // state serves every confirmation this screen needs.
  const [confirmModal, setConfirmModal] = useState(null);

  const loadImplementations = useCallback(async () => {
    if (!id) return;
    try {
      const res = await integrationsService.getImplementations(id);
      setImplementations(res.implementations || []);
      setSlots(res.slots || []);
    } catch (e) {
      setImplError(apiErrorMessage(e, 'Could not load implementations'));
      setImplementations([]);
    }
  }, [id]);

  useEffect(() => {
    if (tab === 'implementations' && !isMcpSourceTemplate && implementations === null) loadImplementations();
  }, [tab, isMcpSourceTemplate, implementations, loadImplementations]);

  // ADDING A PROPERTY — NO WIZARD. Pick the hotel and it is created at once, as a draft, and its board opens: every
  // credential it still needs shows red there and is set by clicking it. The server issues its Universal API
  // credential on create (/opt/utils/uapi-credential). Offered: the template's chain's hotels not already running it.
  const [addHotels, setAddHotels] = useState(null);
  const [addHotelId, setAddHotelId] = useState(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (tab !== 'implementations' || implementations === null) return undefined;
    const accountId = templateAccountId || selectedAccount;
    if (!accountId) { setAddHotels([]); return undefined; }
    let cancelled = false;
    // ONE PIPELINE PER HOTEL: a hotel running ANY pipeline template is not offered (the server refuses it too).
    Promise.all([
      hotelService.getAllHotels({ accountid: accountId }),
      integrationsService.listHotelImplementations({ account_id: accountId }).catch(() => ({ implementations: [] })),
    ])
      .then(([data, running]) => {
        if (cancelled) return;
        const taken = new Set([...implementations, ...(running.implementations || [])].map(i => String(i.hotel_id)));
        setAddHotels((data.hotels || data || [])
          .filter(h => !taken.has(String(h.hotelid)))
          .map(h => ({ id: h.hotelid, label: h.hotelname, code: h.hotelcode || undefined })));
      })
      .catch(() => { if (!cancelled) setAddHotels([]); });
    return () => { cancelled = true; };
  }, [tab, implementations, templateAccountId, selectedAccount]);

  const addImplementation = async () => {
    const hotel = (addHotels || []).find(h => String(h.id) === String(addHotelId));
    if (!hotel) return;
    setAdding(true);
    setImplError(null);
    try {
      const res = await integrationsService.saveImplementation(id, {
        hotelId: hotel.id, name: hotel.label,
        // NOT ACTIVE ON CREATION: it routes nothing until its credentials are set and somebody switches it on.
        status: 'draft', bindings: [],
      });
      setAddHotelId(null);
      await loadImplementations();
      if (res && res.implementation && res.implementation.id) setOpenImplId(res.implementation.id);
    } catch (e) {
      setImplError(apiErrorMessage(e, 'That property could not be added.'));
    } finally {
      setAdding(false);
    }
  };

  const removeImplementation = async (impl) => {
    try {
      await integrationsService.deleteImplementation(id, impl.id);
      setImplementations(list => (list || []).filter(x => x.id !== impl.id));
    } catch (e) {
      setImplError(apiErrorMessage(e, 'Could not remove that implementation'));
    }
  };

  // Client-side ids, replaced by the database's own on save. A number chosen in a browser is not a
  // primary key, and the server rewrites the edges against the ids it gives out.
  const nextNodeId = () => `new-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;

  const fetchSystems = async () => {
    try {
      const d = await integrationsService.listDesignerSystems();
      setGraph(g => ({ ...g, systems: d.systems || [] }));
    } catch (err) {
      console.error('Error fetching systems for the designer:', err);
    }
  };

  // EVERY PATH THAT REWRITES THE DESIGN INVALIDATES THE SLOTS, and there are three: saving an edit, and
  // restoring a version from either of the two places that offer it. All three end in db.saveGraph.
  //
  // Ids are STABLE now — saveGraph updates rows in place — so a box that is still on the diagram keeps
  // its id and the slots keyed by it stay valid. This is still needed for the rest: a route or a system
  // REMOVED from the design is deleted for real, and a slot list read before that removal offers a slot
  // that no longer exists. Filling it fails on the foreign key.
  //
  // A helper rather than the same two lines three times: the first version of this fixed the save path
  // only, and the two restore paths went on handing the wizard dead ids — the same failure, reached by
  // a different button.
  const invalidateImplementations = () => { setImplementations(null); setSlots([]); };

  const fetchGraph = async (integrationId) => {
    try {
      const d = await integrationsService.getGraph(integrationId);
      // EVERY DESIGN HAS A HUB, so one is put there rather than asked for. An integration meets at the
      // Universal API by definition; making somebody add it would be asking a question with one answer.
      const nodes = (d.nodes || []).length
        ? d.nodes
        : [{ id: nextNodeId(), role: 'HUB', marketplace_id: null, adapter: null, label: null }];
      setGraph(g => ({
        nodes,
        edges: d.edges || [],
        routes: d.routes || [],
        customCode: d.customCode || [],
        systems: (d.systems || []).length ? d.systems : g.systems,
        // The owning partner's Universal API vendors (V704) — what Add vendor offers; the save checks the same list.
        vendors: d.vendors || [],
      }));
      setGraphDirty(!(d.nodes || []).length);
      setShapeDirty(false);
    } catch (err) {
      console.error('Error fetching integration design:', err);
    }
  };

  const addNode = (role, system) => {
    setGraph(g => ({
      ...g,
      nodes: [...g.nodes, {
        id: nextNodeId(),
        role,
        marketplace_id: system.id,
        system_name: system.name,
        // The adapter is named by the operator, not guessed from the system: one system sits behind
        // different adapters in different integrations, and the adapter is what is actually deployed.
        // THE ADAPTER COMES FROM THE SYSTEM, not from a question asked here. A system that uses a
        // proxy uses the same one in every integration, and its name is on its marketplace record
        // (V470) — typed per design, the same proxy ended up called three different things.
        adapter: system.uses_proxy === 'Y' ? (system.proxy_name || null) : null,
        uses_proxy: system.uses_proxy,
        label: null,
      }],
    }));
    setGraphDirty(true);
    setShapeDirty(true);
    setSaved(false);
    setPicking(null);
  };

  // A VENDOR'S BOARD (V704): no system and no proxy — the vendor itself, by its Universal API credential.
  const addVendor = (vendor) => {
    setGraph(g => ({
      ...g,
      nodes: [...g.nodes, {
        id: nextNodeId(),
        role: 'VENDOR',
        marketplace_id: null,
        adapter: null,
        label: null,
        vendor_credential_id: vendor.id,
        vendor_name: vendor.name,
        vendor_system_name: vendor.system_name,
        vendor_system_image: vendor.system_image,
      }],
    }));
    setGraphDirty(true);
    setShapeDirty(true);
    setSaved(false);
  };

  // Edits one node in place — today only the proxy's API key, which belongs to this integration's proxy
  // and is saved with the design (V474).
  const updateNode = (nodeId, patch) => {
    setGraph(g => ({ ...g, nodes: g.nodes.map(n => (n.id === nodeId ? { ...n, ...patch } : n)) }));
    setGraphDirty(true);
    setSaved(false);
  };

  const removeNode = (nodeId) => {
    setGraph(g => ({
      ...g,
      nodes: g.nodes.filter(n => n.id !== nodeId),
      // An edge whose end has gone is not an edge. Dropped here as well as server-side, so the canvas
      // never draws an arrow to a box that is no longer on it.
      edges: (g.edges || []).filter(e => e.from_node_id !== nodeId && e.to_node_id !== nodeId),
      // A route whose proxy or backend has been taken off the canvas is not a route. Dropped here as well
      // as server-side, so the lanes on screen match what a save would keep.
      routes: (g.routes || []).filter(r => r.from_node_id !== nodeId && r.to_node_id !== nodeId),
    }));
    setGraphDirty(true);
    setShapeDirty(true);
    setSaved(false);
  };

  useEffect(() => {
    // THE PALETTE ALWAYS, THE DESIGN ONLY WHEN THERE IS ONE. Somebody arrives with a design in mind
    // and the record is the least interesting part of it, so the canvas works before the first save.
    fetchSystems();
    if ((isEditMode || isViewMode) && id) {
      fetchIntegration();
      fetchGraph(id);
    }
  }, [id, isEditMode, isViewMode]);

  // LIVE HEARTBEATS (V489). The design is loaded once, so the To / From badges would only ever count
  // forward from the page load and never show OXI's next call. Every 20s the design is re-read — the
  // same cadence as the sync project page — and ONLY the contact times are copied onto the nodes on
  // screen. Nothing else is taken from the fresh read: an unsaved edit on the canvas must survive a poll.
  //
  // Matched by id, falling back to role + system: a node added since the load carries a browser id the
  // server has never seen, and a save hands every node a new one.
  useEffect(() => {
    if (!(isEditMode || isViewMode) || !id) return undefined;
    const CONTACT = ['http_push_last_contact_ms', 'http_pull_last_contact_ms'];
    const timer = setInterval(async () => {
      try {
        const d = await integrationsService.getGraph(id);
        const fresh = d.nodes || [];
        setGraph(g => {
          let changed = false;
          const nodes = g.nodes.map(n => {
            const match = fresh.find(f => String(f.id) === String(n.id))
              || fresh.find(f => f.role === n.role && String(f.marketplace_id) === String(n.marketplace_id));
            if (!match) return n;
            const patch = {};
            for (const k of CONTACT) if (match[k] !== n[k]) patch[k] = match[k];
            if (!Object.keys(patch).length) return n;
            changed = true;
            return { ...n, ...patch };
          });
          // Unchanged → the same state object, so a quiet poll does not re-render the canvas.
          return changed ? { ...g, nodes } : g;
        });
      } catch (e) { /* keep the last known times */ }
    }, 20000);
    return () => clearInterval(timer);
  }, [id, isEditMode, isViewMode]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await integrationsService.getIntegrationById(id);
      setFormData({
        name: response.integration.name,
        v2_connector_from: response.integration.v2_connector_from,
        v2_connector_to: response.integration.v2_connector_to
      });
      setTemplateChainName(response.integration.account_name || null);
      setTemplateAccountId(response.integration.account_id || null);
      setCanWrite(response.integration.canWrite === true);
      setTemplateMcpSource(response.integration.mcp_source_system_id
        ? { id: response.integration.mcp_source_system_id, name: response.integration.mcp_source_name } : null);
    } catch (err) {
      console.error('Error fetching integration:', err);
      setError('Failed to load integration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      setError('Give the integration a name.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSaved(false);

      // A TEMPLATE IS CREATED FOR A CHAIN. Refused here as well as gated below, because this screen can be
      // reached by its url.
      if (isCreateMode && !selectedAccount && !isMcpSourceTemplate) {
        setError('Choose a chain in the top bar — a pipeline template belongs to a chain.');
        setSaving(false);
        return;
      }

      // An MCP as a Service source system's template has that one owner and no chain (V644).
      const entityScope = isCreateMode && isMcpSourceTemplate
        ? { mcp_source_system_id: Number(mcpSourceParam) }
        : { account_id: selectedAccount };

      if (isCreateMode) {
        // CREATED, THEN DESIGNED. The canvas belongs to an integration, so the record comes first and
        // the designer opens on it — rather than holding a drawing in the browser and hoping the
        // create succeeds.
        const created = await integrationsService.createIntegration({
          name: formData.name,
          ...entityScope,
        });
        const newId = created?.integration?.id || created?.id;
        if (newId) {
          // The canvas drawn before the record existed belongs to it now. Saved here rather than left
          // for the operator to redraw, which is what "save the record first" would have cost them.
          if (graph.nodes.length) {
            await integrationsService.saveGraph(newId, { nodes: graph.nodes, edges: graph.edges || [], routes: graph.routes || [] });
          }
          navigate(`/pipeline-templates/${newId}/edit${location.search}`);
          return;
        }
      } else if (isEditMode) {
        await integrationsService.updateIntegration(id, { name: formData.name });
        // The design is saved with the record: they are one thing to whoever pressed Save, and two
        // buttons would let somebody save the name and lose the picture.
        if (graphDirty) {
          await integrationsService.saveGraph(id, { nodes: graph.nodes, edges: graph.edges || [], routes: graph.routes || [] });
          // Saving reassigns node ids, so the canvas is reloaded from the server rather than kept as drawn —
          // a route or test opened next must name the ids the database holds.
          await fetchGraph(id);
          // AND SO ARE THE SLOTS THE WIZARD FILLS. The canvas was reloaded here and the implementations
          // were not, though both are keyed by the same node and route ids and a save can delete either.
          //
          // `implementations === null` is what the tab's loader treats as "not loaded yet", so clearing
          // it is what makes the tab fetch again instead of showing what it read before the save.
          invalidateImplementations();
        }
        // SAVE STAYS HERE. Designing is iterative — save, test, adjust — and being sent back to the list
        // after every save made each round trip three clicks longer.
        setSaved(true);
      }
    } catch (err) {
      console.error('Error saving integration:', err);
      setError(apiErrorMessage(err, 'Failed to save integration'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(returnPath);
  };

  // DELETING THE TEMPLATE takes everything that hangs off it with it (ON DELETE CASCADE): its versions, custom
  // code and every implementation — so the property count is in the confirmation, not discovered afterwards.
  const handleDelete = () => {
    const n = implementations ? implementations.length : 0;
    setConfirmModal({
      title: `Delete ${formData.name || 'this pipeline template'}`,
      message: 'The template, its versions and its custom code are deleted'
        + (n ? `, along with its ${n} implementation${n === 1 ? '' : 's'} — it stops running at ${n === 1 ? 'that property' : 'those properties'}` : '')
        + '. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        setDeleting(true);
        try {
          await integrationsService.deleteIntegration(id);
          navigate(returnPath);
        } catch (err) {
          console.error('Error deleting integration:', err);
          setError(apiErrorMessage(err, 'The pipeline template could not be deleted'));
          setDeleting(false);
        }
      },
    });
  };

  const pipelinesHeader = inPipelines && (
    <div style={{ padding: '2rem 2.5rem 0' }}>
      <PipelinesHeader
        subtitle={isCreateMode ? 'A new pipeline template for the selected chain.'
          : `${formData.name || 'A pipeline template'}. Its design is what every hotel running it routes with.`}
        tab="integrations"
      />
    </div>
  );

  if (!selectedPartner && !isMcpSourceTemplate) {
    return (
      <div className="main-content">
        <div className="form-container">
          <Card>
            <div className="empty-state">
              <p>Please select a partner to manage integrations.</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ASKED BEFORE THE FORM, not refused after it is filled in. The save refuses this too — it is the one
  // that guarantees it — but letting somebody name a template and draw a design first, only to be told
  // the chain was missing, throws away the work they just did. A CHAIN, NOT A HOTEL: the hotels are the
  // template's implementations, added on its Implementations tab once it exists.
  if (isCreateMode && !selectedAccount && !isMcpSourceTemplate) {
    return (
      <div className="main-content">
        <div className="form-container">
          <div className="form-header"><h1>Create Pipeline Template</h1></div>
          <div className="error-message">
            Choose a chain in the top bar first — a pipeline template belongs to a chain, and runs at the
            hotels you add on its Implementations tab.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="main-content">
        {pipelinesHeader}
        <div className="form-container">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      {pipelinesHeader}
      <div className="form-container">
        <button type="button" className="pp-back-btn" onClick={handleCancel}>
          ← Back to {returnLabel || (templateMcpSource && templateMcpSource.name) || 'MCP as a Service source'}
        </button>
        <div className="form-header">
          <h1>
            {/* PIPELINE TEMPLATE, matching the list this page is reached from — that screen is titled
                "Pipelines", and a form called "Integration" made the two read as different things. */}
            {isCreateMode && 'Create Pipeline Template'}
            {isEditMode && 'Edit Pipeline Template'}
            {isViewMode && 'View Pipeline Template'}
          </h1>
          {/* WHAT THIS INTEGRATION IS BEING MADE FOR, next to its title.
              The scope comes from the top bar and decides who the integration belongs to, but the bar sits
              above every screen and reads as chrome — so the one page where the choice is being ACTED on
              showed no sign of it, and a design saved against the wrong chain looked identical to one
              saved against the right one. Named from the same lists the bar names them from, so the two
              cannot disagree. */}
          {/* The template belongs to a CHAIN (V524). Creating, that is the chain in the top bar; editing,
              it is the record's own, which the bar may not be showing. No hotel: the hotels it runs at are
              its implementations. */}
          {isMcpSourceTemplate ? (
            <div className="intf-scope">
              <span className="intf-scope-label">For</span>
              <span className="intf-scope-chip">MCP as a Service</span>
              {templateMcpSource && templateMcpSource.name && <span className="intf-scope-chip intf-scope-chip-hotel">{templateMcpSource.name}</span>}
            </div>
          ) : (scopePartnerName || (isCreateMode ? scopeAccountName : (templateChainName || scopeAccountName))) && (
            <div className="intf-scope">
              <span className="intf-scope-label">For</span>
              {scopePartnerName && <span className="intf-scope-chip">{scopePartnerName}</span>}
              {(isCreateMode ? scopeAccountName : (templateChainName || scopeAccountName)) && (
                <span className="intf-scope-chip intf-scope-chip-hotel">
                  {isCreateMode ? scopeAccountName : (templateChainName || scopeAccountName)}
                </span>
              )}
            </div>
          )}
        </div>

        {saved && !error && (
          <div className="if-saved" role="status">Saved.</div>
        )}

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <Card>
          <form onSubmit={handleSubmit}>
            <div className="form-section">

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label required">
                    Pipeline Template Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    placeholder="e.g., PMS to Booking Engine"
                    required
                  />
                </div>
              </div>

              {/* TWO THINGS ON ONE RECORD, SO TWO TABS. The design is the TEMPLATE — the shape, which
                  is the same wherever it runs. The implementations are that shape at named properties,
                  each with its own credentials. Keeping them on one page means the shape and the
                  places it runs are read together; separating them into tabs means neither is scrolled
                  past to reach the other. */}
              {/* ONE TAB IS NO TAB BAR: drawn only while there is more than the design to switch to (see TABS). */}
              {!isCreateMode && TABS.length > 1 && (
                <Tabs
                  tabs={[
                    { key: 'design', label: 'Design', icon: <Workflow size={20} /> },
                    { key: 'implementations', label: 'Implementations', icon: <Building2 size={20} />,
                      count: implementations ? implementations.length : undefined },
                  ].filter(t => TABS.includes(t.key))}
                  active={tab}
                  onChange={changeTab}
                />
              )}

              {/* THE DESIGN. Two connector selects could say "this feeds that" and nothing else;
                  what is being built here is several sources, each behind its own adapter, meeting at
                  the Universal API, and several targets beyond it. The canvas is the only place that
                  shape can be stated, so it IS the form. */}
              <div className="form-group" style={{ marginTop: '1rem', display: tab === 'design' || isCreateMode ? 'block' : 'none' }}>
                <label className="form-label">Design</label>

                {/* WHICH DESIGN IS ON SCREEN (V512). The draft is the one that can be edited; a
                    published version is opened to read. Here rather than on the Versions tab because
                    this is where a design is looked at — sending somebody to another tab to answer
                    "what did v1 look like" means they never ask. */}
                {/* ALWAYS, once the versions are known — not only when something has been published.
                    Hidden until the first publish, the one control that says which design is on screen
                    appeared out of nowhere the moment somebody published, and until then there was
                    nothing to say the board was a draft at all. With none published it reads
                    "Draft (editable) · nothing live", which is exactly the situation. */}
                {!isCreateMode && versions && (
                  <div className="ifm-versionbar">
                    <span className="ifm-versionbar-label">Showing</span>
                    <select
                      className="form-control ifm-versionbar-select"
                      value={viewingVersionId || ''}
                      onChange={(e) => setViewingVersionId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Draft (editable)</option>
                      {versions.versions.map(v => (
                        <option key={v.id} value={v.id}>
                          v{v.version}
                          {String(v.id) === String(versions.liveVersionId) ? ' — live' : ''}
                          {v.published_at ? ` · ${formatDateTime(v.published_at)}` : ''}
                        </option>
                      ))}
                    </select>
                    {!viewingDraft && (
                      <>
                        <span className="ifm-versionbar-ro">read-only</span>
                        {isSuperAdmin && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={versionBusy}
                            onClick={() => setConfirmModal({
                              title: `Start the draft again from v${viewingVersion ? viewingVersion.version : ''}`,
                              message: 'The draft is replaced by this version. What is live does not '
                                + 'change, and each property keeps its credentials.',
                              confirmLabel: 'Replace the draft',
                              danger: true,
                              onConfirm: () => {
                                setConfirmModal(null);
                                runVersionAction(async () => {
                                  const res = await integrationsService.restoreVersion(id, viewingVersionId);
                                  if (res.graph) { setGraph(g => ({ ...g, ...res.graph })); setGraphDirty(false); setShapeDirty(false); invalidateImplementations(); }
                                  setViewingVersionId(null);   // back to the draft it was just written into
                                  return null;
                                }, 'The draft could not be replaced');
                              },
                            })}
                          >
                            <RotateCcw size={14} /> Restore into draft
                          </button>
                        )}
                      </>
                    )}
                    {viewingDraft && versions.liveVersionId && (
                      <span className="ifm-versionbar-live">
                        <CircleDot size={12} />
                        {(() => {
                          const live = versions.versions.find(v => String(v.id) === String(versions.liveVersionId));
                          return live ? `v${live.version} live` : 'live';
                        })()}
                      </span>
                    )}
                    {viewingDraft && !versions.liveVersionId && (
                      <span className="ifm-versionbar-ro">nothing live</span>
                    )}
                  </div>
                )}

                {!shownGraph ? <div className="loading-section"><Spinner /></div> : (
                <IntegrationDesigner
                  nodes={shownGraph.nodes}
                  systems={graph.systems}
                  // A PUBLISHED VERSION IS READ-ONLY, like view mode. The board is the same board, so
                  // a pane that accepted clicks it could never save would be the worst way to find that
                  // out — and the server refuses the write regardless.
                  readOnly={isViewMode || !viewingDraft}
                  readOnlyReason={!viewingDraft
                    ? `v${viewingVersion ? viewingVersion.version : ''} is a frozen snapshot. `
                      + 'Switch to the draft to change the design.'
                    : 'This template is open for viewing. Use Edit to change its design.'}

                  onAdd={(role) => setPicking(role)}
                  vendors={graph.vendors || []}
                  // Only on a saved template: which vendors it may have depends on the partner that owns it.
                  onAddVendor={id ? addVendor : undefined}
                  onRemove={removeNode}
                  onUpdateNode={updateNode}
                  credentials={uapiCredentials}
                  routeGrants={routeGrants}
                  access={uapiAccess}
                  hotelId={selectedHotel}
                  accountId={selectedAccount}
                  partnerId={selectedPartner}
                  isSuperAdmin={isSuperAdmin}
                  routes={shownGraph.routes || []}
                  // NOT EDITABLE ON A FROZEN VERSION. Withheld rather than guarded inside, so the board
                  // does not draw the + buttons at all.
                  onRoutesChange={viewingDraft
                    ? ((routes) => { setGraph(g => ({ ...g, routes })); setGraphDirty(true); setShapeDirty(true); })
                    : undefined}
                  customCode={shownGraph.customCode || graph.customCode || []}
                  // CUSTOM CODE OPENS ITS OWN SCREEN, which works on SAVED routes: a route drawn a moment ago
                  // has no id yet, and leaving with unsaved design changes would throw them away. Both say
                  // so rather than opening a screen that cannot find the route.
                  // NO TEST BUTTON HERE. A test is made as a property, with that property's credential
                  // and x-hotelid, and a template has none of those — it is the shape, not a running
                  // pipeline. The button lives on each row of the Implementations tab instead, where
                  // there is something real to call as. Passing no `onTestSource` is what removes it.
                  onEditCode={(route) => {
                    if (!id || !route.id || graphDirty) {
                      setError('Save the integration first — custom code is attached to saved routes, and '
                        + 'leaving now would lose the unsaved design changes.');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      return;
                    }
                    navigate(`/pipeline-templates/${id}/routes/${route.id}/code${location.search || ''}`);
                  }}
                  // HOW THE SOURCE'S EVENTS REACH THE HUB (V686) — polled or streamed, never both; set on the template,
                  // run for every hotel implementing it, each as its own source credential. Each is its own screen (STYLE.md: no modals);
                  // leaving with unsaved design changes would lose them, so it says so instead.
                  // Seen by everyone who can see the template (their own hotels); changed by a superadmin on that screen.
                  onEventDelivery={() => openSourcePage('events')}
                  // THE INTEGRATION'S CUSTOM CODE (V533) — the same button on the other side of the hub.
                  // Superadmin only: it is Universal API custom code, managed on the admin API. Same rule
                  // about saved routes as the proxy's, for the same reason.
                  onEditIntegrationCode={isSuperAdmin ? (route) => {
                    if (!id || !route.id || graphDirty) {
                      setError('Save the integration first — custom code is attached to saved routes, and '
                        + 'leaving now would lose the unsaved design changes.');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      return;
                    }
                    navigate(`/pipeline-templates/${id}/routes/${route.id}/integration-code${location.search || ''}`);
                  } : undefined}
                />
                )}
                <span className="form-help">
                  Each board is one proxy, or one vendor calling the Universal API itself (amber). A route is a row
                  across it: what it carries on the left, the
                  Universal API credential it is made with in the middle, and where that lands on the right.
                  The credential decides the backend, so the targets column is derived rather than drawn.
                  Use <strong>+</strong> between the columns to add a route. The design is saved with the
                  integration.
                </span>

              {/* ── VERSIONS (V512) ──
                  UNDER THE BOARD, NOT IN A TAB OF ITS OWN. Which version is on screen is chosen above
                  the design, so the history belongs beside the design too — the same arrangement the
                  Universal API integration screen uses. A third tab made "publish this" a place to go
                  rather than the next thing to do, and hid the one list that says what is running. */}
              {/* The tab check as well as the container's: the design block is hidden with `display:
                  none` rather than unmounted, so without it this stays mounted under every tab. */}
              {!isCreateMode && tab === 'design' && (
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                    <div>
                      <label className="form-label" style={{ margin: 0 }}>Versions</label>
                      <span className="form-help" style={{ display: 'block', marginTop: '0.2rem' }}>
                        The board above is the draft. Publishing freezes it as a numbered version;
                        making one live is a separate step, so a version can be read before anything
                        runs it and a rollback is a pointer move rather than an edit under an outage.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={graphDirty || versionBusy || !isSuperAdmin}
                      title={graphDirty
                        ? 'Save the design first — a version is a snapshot of what is stored, not of what is on screen'
                        : !isSuperAdmin
                          ? 'Only a superadmin can publish a version'
                          : 'Freeze the current design as the next version'}
                      onClick={() => setConfirmModal({
                        title: 'Publish this design',
                        message: 'The design as it is stored now is frozen as the next version. Nothing '
                          + 'changes for any property until that version is made live.',
                        confirmLabel: 'Publish',
                        onConfirm: () => {
                          setConfirmModal(null);
                          runVersionAction(() => integrationsService.publishVersion(id),
                            'That version could not be published');
                        },
                      })}
                      style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      <Plus size={16} /> Publish
                    </button>
                  </div>

                  {implError && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{implError}</div>}

                  {versions === null ? (
                    <div className="loading-section" style={{ marginTop: '1rem' }}><Spinner /></div>
                  ) : (
                    <div style={{ marginTop: '1rem' }}>
                      <VersionsTable
                        versions={versions.versions}
                        liveVersionId={versions.liveVersionId}
                        sizeLabel="Routes"
                        sizeOf={(v) => v.route_count ?? 0}
                        // HOW MANY PROPERTIES ARE ON EACH (V672) — every implementation routes with its own version.
                        usageLabel="Implementations"
                        usageOf={(v) => v.implementation_count ?? 0}
                        // THE DRAFT IS THE DESIGN TAB, not a row in the table — so its size is counted
                        // from what the designer is holding rather than fetched again.
                        draft={{ size: (graph.routes || []).length }}
                        busy={versionBusy}
                        onMakeLive={(v) => setConfirmModal({
                          title: `Make v${v.version} live`,
                          // GOING LIVE MOVES NOBODY ALREADY ON A VERSION (V672) — each is moved on the Implementations tab.
                          message: `New implementations start on v${v.version}, and any not on a version yet move to it. `
                            + 'Implementations already on a version stay where they are until you move each one on '
                            + 'the Implementations tab. The draft is not changed.',
                          confirmLabel: 'Make live',
                          onConfirm: () => {
                            setConfirmModal(null);
                            runVersionAction(() => integrationsService.makeVersionLive(id, v.id),
                              'That version could not be made live');
                          },
                        })}
                        onTakeOffline={() => setConfirmModal({
                          title: 'Take this template offline',
                          message: 'No version is live afterwards, so this template runs nowhere until '
                            + 'one is made live again.',
                          confirmLabel: 'Take offline',
                          danger: true,
                          onConfirm: () => {
                            setConfirmModal(null);
                            runVersionAction(
                              () => integrationsService.takeVersionOffline(id, versions.liveVersionId),
                              'This template could not be taken offline');
                          },
                        })}
                        onRestore={isSuperAdmin ? (v) => setConfirmModal({
                          title: `Start the draft again from v${v.version}`,
                          message: 'The design on the Design tab is replaced by this version. What is '
                            + 'live does not change, and each property keeps its credentials.',
                          confirmLabel: 'Replace the draft',
                          danger: true,
                          onConfirm: () => {
                            setConfirmModal(null);
                            runVersionAction(async () => {
                              const res = await integrationsService.restoreVersion(id, v.id);
                              // The board has to show what was just restored, or the next save would
                              // write the old design back over it.
                              if (res.graph) { setGraph(g => ({ ...g, ...res.graph })); setGraphDirty(false); setShapeDirty(false); invalidateImplementations(); }
                              return null;
                            }, 'The draft could not be replaced');
                          },
                        }) : undefined}
                        onDelete={isSuperAdmin ? (v) => setConfirmModal({
                          title: `Delete v${v.version}`,
                          message: 'This snapshot is removed. Nothing running changes — a live version '
                            + 'cannot be deleted.',
                          confirmLabel: 'Delete',
                          danger: true,
                          onConfirm: () => {
                            setConfirmModal(null);
                            runVersionAction(() => integrationsService.deleteVersion(id, v.id),
                              'That version could not be deleted');
                          },
                        }) : undefined}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

              {/* ── IMPLEMENTATIONS ──────────────────────────────────────────────────────────────
                  The template is credential-free by intent; this is where the credentials live, one
                  group per property. Each row is the same pipeline running somewhere else. */}
              {!isCreateMode && !isMcpSourceTemplate && tab === 'implementations' && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                    <div>
                      <label className="form-label" style={{ margin: 0 }}>Implementations</label>
                      <span className="form-help" style={{ display: 'block', marginTop: '0.2rem' }}>
                        The template above says what this pipeline is. An implementation is that
                        pipeline at one property, with that property&rsquo;s credentials —
                        {slots.length ? ` ${slots.length} slot${slots.length === 1 ? '' : 's'} to fill` : ' nothing to fill yet'}.
                      </span>
                    </div>
                    {/* PICK THE PROPERTY, ADD — created at once and opened; its credentials are set on its board. Disabled
                        while the design's SHAPE has unsaved changes: an implementation fills the SAVED slots. */}
                    <div className="ifm-impl-add">
                      <SearchSelect
                        items={addHotels || []}
                        value={addHotelId}
                        onChange={setAddHotelId}
                        disabled={shapeDirty || !slots.length || adding || addHotels === null}
                        placeholder={addHotels === null ? 'Loading properties…' : 'Choose a property…'}
                        searchPlaceholder="Search properties…"
                        emptyLabel="Every hotel in this chain already runs a pipeline — a hotel runs one."
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addImplementation}
                        disabled={shapeDirty || !slots.length || !addHotelId || adding}
                        title={shapeDirty
                          ? 'Save the design first — an implementation fills the slots the saved template has'
                          : !slots.length
                            ? 'Draw at least one source or route first; there is nothing to fill yet'
                            : 'Add this property — then set each red credential on its board'}
                        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        <Plus size={16} /> {adding ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                  </div>

                  {implError && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{implError}</div>}

                  {implementations === null ? (
                    <div className="loading-section" style={{ marginTop: '1rem' }}><Spinner /></div>
                  ) : implementations.length === 0 ? (
                    <p className="form-help" style={{ marginTop: '1rem' }}>
                      No property runs this template yet. Choose one and <strong>Add</strong> it — its board opens, and
                      each credential it still needs shows red until you click it and set it.
                    </p>
                  ) : (
                    <table className="acp-table" style={{ marginTop: '1rem' }}>
                      <thead>
                        <tr><th aria-label="Open" /><th>Property</th><th>Name</th><th>Status</th><th>Version</th><th>Filled</th>
                            <th aria-label="Actions" /></tr>
                      </thead>
                      <tbody>
                        {implementations.map(im => (
                          <React.Fragment key={im.id}>
                          <tr
                            className={`ifm-impl-row${openImplId === im.id ? ' is-open' : ''}`}
                            onClick={() => setOpenImplId(openImplId === im.id ? null : im.id)}
                          >
                            {/* THE WHOLE ROW OPENS IT, and the chevron says so. A row that is a link to
                                a diagram and gives no sign of it is a diagram nobody finds. */}
                            <td style={{ width: '1.5rem' }}>
                              {openImplId === im.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{im.hotelname || <em>no property</em>}</div>
                              {im.chain_name && <div className="form-help" style={{ margin: 0 }}>{im.chain_name}</div>}
                            </td>
                            <td>{im.name}</td>
                            <td onClick={e => e.stopPropagation()}>
                              {/* SWITCHED ON HERE. The wizard saves a draft and says it routes nothing until
                                  somebody makes it active — and there was nowhere to do that, so every
                                  implementation stayed a draft and the proxy skipped its property. Draft is
                                  the normal state of something half-configured, so it is not coloured like a
                                  fault. Active needs every slot filled: the proxy would otherwise route with
                                  the template's own (usually empty) credentials for the missing ones. */}
                              <ImplementationStatusSelect
                                impl={im}
                                disabled={statusSaving === im.id || isViewMode}
                                onChange={(status) => changeImplStatus(im, status)}
                              />
                            </td>
                            {/* THE VERSION THIS PROPERTY ROUTES WITH (V672). Making a version live moves nobody
                                already on one, so each property is moved here — and the hint says when it is behind
                                the live one. "Draft" only for a template never published: it routes the draft. */}
                            <td onClick={e => e.stopPropagation()}>
                              <ImplementationVersionSelect
                                impl={im}
                                versions={publishedVersions}
                                liveVersionId={versions && versions.liveVersionId}
                                disabled={versionSaving === im.id || isViewMode}
                                onChange={(versionId) => changeImplVersion(im, versionId)}
                              />
                            </td>
                            <td className={im.filled < im.slots ? 'iw-empty' : ''}>
                              {im.filled} of {im.slots}
                            </td>

                            {/* NO TEST COLUMN. Test belongs on the System box of the board below — that
                                is where it always was, and on a board drawn AS THIS PROPERTY it is now
                                also correct. A column of Test buttons made it a property of the row
                                rather than of the source it actually sends through. */}
                            <td style={{ textAlign: 'right' }}>
                              <button
                                type="button"
                                title={`Remove ${im.name}`}
                                aria-label={`Remove ${im.name}`}
                                onClick={(e) => { e.stopPropagation(); setConfirmModal({
                                  title: 'Remove this implementation',
                                  message: `Stop ${im.hotelname || 'this property'} running this template? `
                                    + 'The credentials it names are not deleted — only the fact that this '
                                    + 'template uses them here.',
                                  confirmLabel: 'Remove',
                                  danger: true,
                                  onConfirm: () => { setConfirmModal(null); removeImplementation(im); },
                                }); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer',
                                         color: '#ef4444', padding: '0.25rem' }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>

                          {/* ── THIS PROPERTY'S PIPELINE, DRAWN ── */}
                          {openImplId === im.id && (
                            <tr className="ifm-impl-board-row">
                              <td colSpan={7}>
                                <ImplementationBoard
                                  templateId={id}
                                  implementationId={im.id}
                                  graph={graph}
                                  status={im.status}
                                  accountId={selectedAccount}
                                  partnerId={selectedPartner}
                                  isSuperAdmin={isSuperAdmin}
                                  onSaved={implSaved}
                                  onError={implBoardError}
                                  // TEST, STREAMING CLIENT, EVENT POLLER, QUEUE AND FILES are the board's own (superadmin) — the
                                  // same on every screen that draws an implementation.
                                >
                                  <span className="form-help">
                                    The template as <strong>{im.hotelname || im.name}</strong> runs it.
                                    The shape is the design; the credentials, x-hotelids and backends
                                    are this property&rsquo;s. Change the shape on the Design tab;
                                    change what fills it with <strong>Add</strong>.
                                  </span>
                                </ImplementationBoard>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}


            </div>

            {/* PICKING A SYSTEM. A full-screen list would lose the canvas behind it, and this is a
                choice made while looking at the diagram. It was a panel beside the canvas for that reason
                and became a long unsearchable column that pushed the canvas off the screen; it is a modal
                now, with a search box, and the canvas comes straight back. Which systems are offered, and
                what each empty list means, is the modal's own business. */}
            {confirmModal && (
              <ConfirmModal
                title={confirmModal.title}
                message={confirmModal.message}
                confirmLabel={confirmModal.confirmLabel}
                danger={confirmModal.danger}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(null)}
              />
            )}

            <PickSystemModal
              isOpen={!!picking}
              role={picking}
              systems={graph.systems || []}
              onPick={(sys) => addNode(picking, sys)}
              onClose={() => setPicking(null)}
            />

            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
              >
                {isViewMode ? 'Back' : 'Cancel'}
              </Button>
              {/* NOT WHILE A FROZEN VERSION IS ON SCREEN. Save writes the DRAFT, so pressing it here
                  would write back whatever the draft still holds — which is not what is being looked
                  at. Switch to the draft, or restore this version into it. */}
              {!isViewMode && viewingDraft && (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (isCreateMode ? 'Create Integration' : 'Save Changes')}
                </Button>
              )}
              {!isViewMode && !viewingDraft && (
                <span className="form-help" style={{ alignSelf: 'center' }}>
                  Showing a published version. Switch to <strong>Draft</strong> to make changes.
                </span>
              )}
              {!isCreateMode && canWrite && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="if-delete-btn"
                >
                  <Trash2 size={14} /> {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default IntegrationForm;
